import { c as _c } from "react/compiler-runtime";
import { useTranslations } from "next-intl";
import { Icon } from "@repo/ui/icon";
import { LinkButton } from "@repo/ui/link-button";
import { cn } from "@repo/ui/utils";
import { RichTextBodyCopySimple } from "@/components/common/rich-text/rich-text-body-copy-simple.component";
import { VisualWrapper } from "@/components/common/visual/visual.component";
import type { PersonCardData } from "./image-card-grid.types";
interface PersonCardPanelProps {
  /** Portrait image shown on the left (or on top at mobile). */
  visual: PersonCardData["visual"];
  /** Full name shown at the top of the details block. */
  personName: PersonCardData["personName"];
  /** Optional role / job title shown beneath the name. */
  role: PersonCardData["role"];
  /** Biography rendered via `RichText` below the role. */
  bio: PersonCardData["bio"];
  /** Optional LinkedIn profile URL rendered as a link-button. */
  linkedinUrl: PersonCardData["linkedinUrl"];
  /** Additional CSS class names merged onto the panel root. */
  className?: string;
}

/**
 * Content rendered inside the PersonCard slide-out panel (DialogContent).
 *
 * Matches Figma node 1120-126421: square-ish portrait on the left, name +
 * role + biography on the right, LinkedIn link-button below. Radix Dialog
 * handles focus management, ESC to close, and body scroll lock.
 */
export function PersonCardPanel(t0) {
  const $ = _c(26);
  const {
    visual,
    personName,
    role,
    bio,
    linkedinUrl,
    className
  } = t0;
  const t = useTranslations("personCard");
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex flex-col gap-8 p-6 desktop:p-16", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== visual) {
    t2 = visual && <div className="relative aspect-[212/207] w-full max-w-[212px] shrink-0 overflow-hidden rounded-md"><VisualWrapper visual={visual} className="size-full" visualClassName="size-full" imgClassName="object-cover" /></div>;
    $[2] = visual;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== personName) {
    t3 = <h3 className="typography-mobile-heading-subtitle-1 desktop:typography-desktop-heading-subtitle-1 text-text-primary">{personName}</h3>;
    $[4] = personName;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== role) {
    t4 = role && <p className="typography-body-sm-regular text-text-secondary">{role}</p>;
    $[6] = role;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = <div className="flex flex-col gap-2">{t3}{t4}</div>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== bio) {
    t6 = bio && <RichTextBodyCopySimple richText={bio} as="body-md-sm" className="text-text-secondary" />;
    $[11] = bio;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== linkedinUrl || $[14] !== t) {
    t7 = linkedinUrl && <div className="flex items-center gap-2"><Icon name="linkedin" className="size-6" /><LinkButton asChild={true} underline={true} size="sm"><a href={linkedinUrl} target="_blank" rel="noopener noreferrer">{t("viewLinkedIn")}</a></LinkButton></div>;
    $[13] = linkedinUrl;
    $[14] = t;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t5 || $[17] !== t6 || $[18] !== t7) {
    t8 = <div className="flex flex-1 flex-col gap-6">{t5}{t6}{t7}</div>;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t2 || $[21] !== t8) {
    t9 = <div className="flex flex-col gap-6 tablet:flex-row tablet:items-start tablet:gap-[59px]">{t2}{t8}</div>;
    $[20] = t2;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t1 || $[24] !== t9) {
    t10 = <div className={t1} data-testid="person-card-panel">{t9}</div>;
    $[23] = t1;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  return t10;
}